import { Add, Delete, Lock } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteMemberProfile } from 'src/shared/services/memberService';
import { Member, MemberProfileData } from '../models/member';
import { MemberAddProfile } from './memberAddProfile';

type MemberProfilesProps = {
  member: Member;
  callback: () => void; // OLD in member: (val) => setTimeStamp(val)
};

export const MemberProfiles = (props: MemberProfilesProps) => {
  const { member, callback } = props;
  const { t } = useTranslation(['member', 'common']);
  const isMounted = useIsMounted();
  const [showProfileEdit, setShowProfileEdit] = useState<boolean>(false);

  const [memberProfileIdToDelete, setMemberProfileIdToDelete] = useState<string>('');

  const handleDeleteMemberProfile = async (memberProfileId: string) => {
    try {
      const res = await deleteMemberProfile(member.member_id || 0, memberProfileId);
      if (isMounted()) {
        ConsoleHelper.log('res deleteMemberProfile', res);
        toast.success(<>{t('member:editMember.profileDeleted')}</>);
        setMemberProfileIdToDelete('');
        callback();
        // callback(new Date().getUTCMilliseconds().toString());
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    }
  };

  const handleProfileAdded = () => {
    callback();
    setShowProfileEdit(false);
    // callback(new Date().getUTCMilliseconds().toString());
  };

  const TableWrapper = ({ children }) => {
    return (
      <TableContainer>
        <Table
          size='small'
          sx={{
            '& th': {
              whiteSpace: 'nowrap',
              pr: '1rem !important',
            },
            '& td': {
              width: '100%',
            },
            '& td, & th': { border: 0 },
          }}
        >
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  const tabelRow = (label: string, value: string | undefined, removeId?: string, locked?: boolean) => {
    if (!value || locked) return null; // Låsta profiler visas i formuläret på redigera, däljer from från profile boxen. Sofie 2023-02-09
    return (
      value && (
        <TableRow key={`${label}-${value}-${removeId}`}>
          <TableCell component={'th' as any} scope='row' sx={{ verticalAlign: 'top' }}>
            <Typography variant='body1' sx={{ fontWeight: 600 }}>
              {label}:
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant='body1'>{value}</Typography>
          </TableCell>
          {removeId && (
            <TableCell padding='none' sx={{ p: '0 !important' }}>
              {locked && (
                <Tooltip arrow title={<>{t('member:memberDetails.lockedProfile')}</>} placement='top'>
                  <span>
                    <IconButton size='small' disabled>
                      <Lock fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!locked && (
                <Tooltip arrow title={<>{t('common:button.delete')}</>} placement='top'>
                  <span>
                    <IconButton size='small' onClick={() => setMemberProfileIdToDelete(removeId)}>
                      <Delete fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </TableCell>
          )}
        </TableRow>
      )
    );
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs={12}>
          <Card sx={{ p: 1, mx: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', alignContent: 'flex-start' }}>
              <Typography variant='h6' sx={{ px: 1 }} gutterBottom>
                {t('member:tableHeaders.profiles')}{' '}
                <small style={{ fontWeight: 100 }}>({member.profileData ? member.profileData.filter((profile) => !profile.locked).length : 0})</small>
              </Typography>
              <Button
                variant='outlined'
                size='small'
                endIcon={<Add fontSize='small' />}
                onClick={() => setShowProfileEdit(true)}
                disabled={member.updateRequest}
              >
                {t('common:button.add')}
              </Button>
            </Box>
            <Divider />
            {showProfileEdit && member.member_id && (
              <MemberAddProfile callback={() => handleProfileAdded()} closeEdit={() => setShowProfileEdit(false)} member={member} />
            )}
            <TableWrapper>
              {member.profileData &&
                member.profileData
                  .sort((a: MemberProfileData, b: MemberProfileData) => {
                    if (a.groupName < b.groupName) {
                      return -1;
                    }
                    if (a.groupName > b.groupName) {
                      return 1;
                    }
                    return 0;
                  })
                  .map((prof) => {
                    return tabelRow(prof.groupName, prof.itemName, prof.memberProfileItem_id.toString(), prof.locked);
                  })}
            </TableWrapper>
          </Card>
        </Grid>
      </GridWrapper>
      <Dialog maxWidth='xs' open={!!memberProfileIdToDelete}>
        <DialogTitle>Radera profilen?</DialogTitle>
        <DialogActions>
          <Button autoFocus variant='outlined' onClick={() => setMemberProfileIdToDelete('')}>
            {t('common:button.cancel')}
          </Button>
          <Button variant='contained' color='primary' disableElevation onClick={() => handleDeleteMemberProfile(memberProfileIdToDelete)}>
            {t('common:button.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
